import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import LoaderOverlay from "../LoaderOverlay";
import OgLogo from "../../assets/BlackLogo.png";
// Hooks
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../hooks";

// Actions
import {
  changePage,
  changeSubjectPage,
  fetchArticles,
  getArticlesByTag,
  getArticlesByCategory,
  getTotalArticles,
  changeTagPage,
  getCategories,
  getTags,
  getArticlesByCategoryCaseStudy,
} from "../../store/actions";

// Types
import { Article } from "../../store/actions/types";

// Components
import Container from "../Container";
import ArticleCard from "../ArticleCard";
import Loader from "../Loader";
import Pagination from "../Pagination";
import Tags from "../Tags";
import Input from "../SearchInput";

// Style
import * as S from "./style";
import Categories from "../Categories";
import { RAZZLE_APP_PORTAL_LINK } from "../../constants";
import { cleanArticle } from "../../store/reducers/articles";
import ArticleNotFound from "../../pages/ArticleNotFound";
import CaseStudyGrid from "../CaseStudyGrid";
import BackToTop from "../BackToTop";

export interface RouterState {
  category: string;
  author: string;
  tag: string;
  search: string;
  id: string;
}

const Grid: React.FC = () => {
  const location = useLocation<RouterState>();
  const articlesPerPage = location.pathname.includes("/case-study") ? 9 : 6;
  let [page, setPage] = useState(0);
  const { category } = useParams<RouterState>();
  const { tag } = useParams<RouterState>();
  const { search } = location;
  const history = useHistory();
  const dispatch = useAppDispatch();

  const total = useAppSelector((state) => state.articles.count); //ricavo il totale degli articoli per facilitarmi con la filterBar in CaseStudyGrid
  let filter = {
    category,
    tag,
    articlesPerPage,
    totalArticles: total,
    search,
    page,
  };

  const { error } = useAppSelector((state) => state.articles);
  const { articles, tags, categories } = useAppSelector(
    (state) => state.articles.data
  );
  const isCaseStudy = location.pathname.includes("/case-study");

  useEffect(() => {
    if (isCaseStudy) return;

    dispatch(cleanArticle());

    if (categories.length <= 0 && tags.length <= 0) {
      dispatch(getCategories());
      dispatch(getTags());
    }

    if (tag && !search && page <= 0) {
      return dispatch(getArticlesByTag({ tag, articlesPerPage }));
    }

    if (!category && !search && page <= 0) {
      return dispatch(fetchArticles(articlesPerPage));
    }

    if (category && !search && page <= 0) {
      return dispatch(getArticlesByCategory({ category, articlesPerPage }));
    }

  }, [category, tag, search, page, dispatch, location.pathname]);

  useEffect(() => {
    if (isCaseStudy) return;
    if (!category && !tag && search) {
      const pageOnRefresh = Number(search.split("=")[1]);
      setPage((page = pageOnRefresh));
      dispatch(changePage({ ...filter, page }));
    }

    if (category && search) {
      const pageOnRefresh = Number(search.split("=")[1]);
      setPage((page = pageOnRefresh));
      dispatch(changeSubjectPage({ ...filter, page }));
    }

    if (tag && search) {
      const pageOnRefresh = Number(search.split("=")[1]);
      setPage((page = pageOnRefresh));
      dispatch(changeTagPage({ ...filter, page }));
    }

    if (!search) {
      setPage(0);
    }
  }, [category, search, dispatch]);

  useEffect(() => {
    if (page === 0 && !category) {
      history.replace("/magazine", {
        pathname: "/magazine?page=0",
        isActive: true,
      });
    }

    if (page === 0 && category) {
      history.replace(`/${category}`, {
        pathname: `/${category}?page=0`,
        isActive: true,
      });
    }

    if (page === 0 && tag) {
      history.replace(`/tag/${tag}`, {
        pathname: `/tag/${tag}?page=0`,
        isActive: true,
      });
    }
  }, [page, history, category, tag]);

  let [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    if (firstRender === true) {
      setFirstRender(false);
    } else {
      window && window.scrollTo({ top: 0 });
    }
  }, [page]);

  useEffect(() => {
    if (location.pathname.includes("/case-study")) return;
  }, [category, tag, page, search, dispatch, location.pathname]);

  const mainArticles = articles
    ? articles
        .slice(0, 2)
        .map(
          ({
            id,
            title,
            description,
            image,
            image_caption,
            effective,
            subjects,
            text,
            parent,
            creators,
          }: Article) => (
            <ArticleCard
              isMain
              isOneArticle={articles.length === 1}
              key={id}
              id={id}
              title={title}
              description={description}
              text={text}
              image={image}
              image_caption={image_caption}
              effective={effective}
              creators={creators}
              subjects={subjects}
              parent={parent}
            />
          )
        )
    : [];

  const secondaryArticles = articles
    ? articles
        .slice(2, articles.length)
        .map(
          ({
            id,
            title,
            effective,
            subjects,
            text,
            parent,
            creators,
          }: Article) => (
            <ArticleCard
              isSecondary
              key={id}
              id={id}
              title={title}
              text={text}
              effective={effective}
              creators={creators}
              subjects={subjects}
              parent={parent}
            />
          )
        )
    : [];

  return (
    <>
      <Helmet>
        <title>Unitiva - Magazine</title>
        <meta
          name="description"
          content="Consulenza Plone, Plone Consulting, Open Source, App Mobile, Web App, eCommerce, Portali web"
        />
        <meta property="og:title" content="Unitiva - Magazine" />
        <meta
          property="og:description"
          content="Consulenza Plone, Plone Consulting, Open Source, App Mobile, Web App, eCommerce, Portali web"
        />
        <meta
          property="og:image"
          content={`${RAZZLE_APP_PORTAL_LINK}/theme_assets/images/BlackLogo.png`}
        />
        <meta property="og:image:type" content="image/png" />
        <meta
          property="og:url"
          content={
            typeof window !== "undefined"
              ? window.location.href
              : `${RAZZLE_APP_PORTAL_LINK}${location.pathname}`
          }
        />
        <link
          rel="canonical"
          href={
            typeof window !== "undefined"
              ? window.location.href
              : `${RAZZLE_APP_PORTAL_LINK}${location.pathname}`
          }
        />
      </Helmet>
      <LoaderOverlay />
      {!location.pathname.includes("/case-study") ? (
        <Container>
          {articles.length <= 0 ? (
            <>
              {error.name === "" ? (
                <Loader />
              ) : (
                <ArticleNotFound title="No articles found" />
              )}
            </>
          ) : (
            <S.ContainerGrid>
              <S.Articles>
                <S.MainArticlesGrid>{mainArticles}</S.MainArticlesGrid>
                <S.SecondaryArticlesGrid>
                  {secondaryArticles}
                </S.SecondaryArticlesGrid>
                <Pagination
                  category={category}
                  tag={tag}
                  articlesPerPage={articlesPerPage}
                  page={page}
                  filteredArticlesCount={0}
                  theme={null}
                  setPage={setPage}
                />
              </S.Articles>
              <S.Filters>
                <Input />
                <Categories categories={categories} />
                <Tags subjects={[...new Set(tags)]} />
              </S.Filters>
            </S.ContainerGrid>
          )}
        </Container>
      ) : (
        <CaseStudyGrid />
      )}
      <BackToTop />
    </>
  );
};

export default Grid;
